import type { AccountPermission } from '@customerio/hydra-permissions';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import { accountPermissionsForRole, type RoleDefinition } from '@customerio/hydra-permissions/roles';
import type { PermissionsUser } from '@customerio/hydra-permissions/validation';
import { isStandardRole, type useRoleDefinitions } from '@/features/roles';
import type { AugmentedUser } from '@/services/auth/types';

// What `checkPermission` reads, built from hydra's user and role definitions. Shared by
// `DerivedPermissionsProvider` and the check for code outside React, so both give the same answer.

/** The user in the shape `checkPermission` reads. */
export function buildPermissionsUser(user: AugmentedUser): PermissionsUser {
  return {
    account_permissions: user.account_permissions,
    workspace_roles: buildWorkspaceRoles(user),
    masq_permissions: user.masq_permissions ?? UserMasqPermission.NoMasqAccess,
  };
}

function buildWorkspaceRoles(user: AugmentedUser): Record<number, { id?: number; name: string }> | null {
  if (user.roles == null) {
    return null;
  }

  const workspaceRoles: Record<number, { id?: number; name: string }> = {};
  for (const userRole of user.roles) {
    workspaceRoles[userRole.workspace_id] = { id: userRole.role_id, name: userRole.role_name };
  }
  return workspaceRoles;
}

/** The account's role definitions in the shape `checkPermission` reads, keyed by role name. */
export function buildPermissionRoles(
  roleDefinitions: ReturnType<typeof useRoleDefinitions>['data'],
  user: AugmentedUser | null,
): Record<string, RoleDefinition> | null {
  // `checkPermission` reads null as "no role data" and an empty object as "this
  // account has no roles". Both deny, but only one of them is an answer.
  if (roleDefinitions == null) {
    return null;
  }

  const roles: Record<string, RoleDefinition> = {};
  for (const role of Object.values(roleDefinitions)) {
    // Custom roles don't carry account permissions, so we populate what we have from the user.
    const accountPermissions: Set<AccountPermission> = isStandardRole(role.name)
      ? accountPermissionsForRole(role.name)
      : new Set(user?.account_permissions ?? []);

    // Role names are unique.
    roles[role.name] = {
      ...role,
      accountPermissions,
    };
  }
  return roles;
}
